import { Link } from 'react-router-dom'
import DashboardPreview from '../components/DashboardPreview'
import Card from '../components/Card'

const paymentUrl = import.meta.env.VITE_ICA_DODO_PAYMENT_LINK_URL

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center rounded-full border border-ica-border bg-ica-surface px-4 py-1.5 text-xs font-medium text-ica-text-secondary">
            <span className="mr-2 h-1.5 w-1.5 rounded-full bg-ica-accent" />
            For freelancers who hate chasing payments
          </div>

          <h1 className="mb-5 text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl">
            Stop awkwardly chasing
            <br />
            <span className="text-ica-accent">unpaid invoices.</span>
          </h1>

          <p className="mx-auto mb-8 max-w-xl text-base leading-relaxed text-ica-text-secondary md:text-lg">
            Add your client, invoice amount, due date, and invoice link. Invoice Chase Assistant sends polite follow-up emails automatically until it is paid.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/invoices/new"
              className="rounded-xl bg-ica-accent px-6 py-3 text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90"
            >
              Set up my first follow-up
            </Link>
            <button
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-xl border border-ica-border px-6 py-3 text-sm font-medium text-ica-text-secondary transition-colors hover:text-ica-text"
            >
              See how it works
            </button>
          </div>

          <p className="mt-6 text-xs text-ica-text-muted">
            No accounting software. No client portal. Just polite follow-ups.
          </p>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="pb-20">
        <DashboardPreview />
      </section>

      {/* Problem */}
      <section id="problem" className="py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="mb-8 text-3xl font-bold tracking-tight md:text-4xl">
            Getting paid should not require awkward follow-up emails.
          </h2>
          <div className="space-y-4 text-left">
            {[
              'You finish the work.',
              'You send the invoice.',
              'Then the client goes quiet.',
              'You waste energy writing "just checking in" emails.',
              'The tool handles the follow-up sequence for you.',
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl border border-ica-border bg-ica-surface p-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ica-accent-soft text-sm font-bold text-ica-accent">
                  {i + 1}
                </span>
                <p className="text-sm font-medium text-ica-text-secondary">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-12 text-center text-3xl font-bold tracking-tight md:text-4xl">How it works</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { step: '01', title: 'Add invoice details', desc: 'Client, amount, due date, and invoice link.' },
              { step: '02', title: 'Review the timeline', desc: 'See exactly when reminders will be sent.' },
              { step: '03', title: 'Automatic emails', desc: 'Polite follow-ups send on schedule.' },
              { step: '04', title: 'Mark paid', desc: 'One click stops all reminders.' },
            ].map((item) => (
              <Card key={item.step} elevated className="relative overflow-hidden">
                <span className="absolute right-4 top-4 text-4xl font-bold text-ica-text-muted/20">{item.step}</span>
                <div className="relative z-10 space-y-2 pt-8">
                  <p className="text-sm font-semibold">{item.title}</p>
                  <p className="text-xs leading-relaxed text-ica-text-muted">{item.desc}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reminder timeline preview */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-12 text-center text-3xl font-bold tracking-tight md:text-4xl">Your reminder sequence</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { offset: '3 days before', title: 'Friendly reminder', desc: 'A gentle heads-up before the due date.' },
              { offset: '1 day overdue', title: 'Quick overdue reminder', desc: 'Polite nudge that payment is now late.' },
              { offset: '7 days overdue', title: 'Firm follow-up', desc: 'More direct, still professional.' },
              { offset: '14 days overdue', title: 'Final polite reminder', desc: 'Last attempt before escalation.' },
            ].map((item, i) => (
              <Card key={i} elevated className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-ica-accent" />
                  <span className="text-xs font-medium text-ica-text-muted">{item.offset}</span>
                </div>
                <p className="text-sm font-semibold">{item.title}</p>
                <p className="text-xs leading-relaxed text-ica-text-muted">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="mx-auto max-w-sm text-center">
          <h2 className="mb-12 text-3xl font-bold tracking-tight md:text-4xl">Pricing</h2>
          <Card elevated className="space-y-6 p-8">
            <div>
              <span className="inline-flex items-center rounded-full bg-ica-accent-soft px-3 py-1 text-xs font-semibold text-ica-accent">
                Beta
              </span>
              <p className="mt-4 text-4xl font-bold">$9<span className="text-lg font-medium text-ica-text-muted">/month</span></p>
              <p className="mt-2 text-sm text-ica-text-muted">
                For freelancers who want payment follow-ups handled automatically.
              </p>
            </div>
            <ul className="space-y-3 text-left text-sm">
              {[
                'Up to 25 active invoices',
                'Automatic reminder sequence',
                'Pause anytime',
                'Mark paid anytime',
                'Polite templates included',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-ica-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-ica-text-secondary">{f}</span>
                </li>
              ))}
            </ul>
            {paymentUrl ? (
              <a
                href={paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full rounded-xl bg-ica-accent py-3 text-center text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90"
              >
                Start beta
              </a>
            ) : (
              <button
                disabled
                className="block w-full cursor-not-allowed rounded-xl bg-ica-elevated py-3 text-center text-sm font-medium text-ica-text-muted"
              >
                Start beta (not configured)
              </button>
            )}
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-2xl">
          <h2 className="mb-12 text-center text-3xl font-bold tracking-tight md:text-4xl">FAQ</h2>
          <div className="space-y-3">
            {[
              { q: 'Is this an invoicing app?', a: 'No. Use your existing invoice link or PDF.' },
              { q: 'Does it process payments?', a: 'No. It only helps with follow-up reminders.' },
              { q: 'Can I stop reminders?', a: 'Yes. Mark paid or pause anytime.' },
              { q: 'Will it sound aggressive?', a: 'No. Templates are polite and professional.' },
            ].map((item, i) => (
              <Card key={i} className="space-y-2">
                <p className="text-sm font-semibold">{item.q}</p>
                <p className="text-sm text-ica-text-muted">{item.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-xl text-center">
          <h2 className="mb-6 text-3xl font-bold tracking-tight md:text-4xl">Ready to stop chasing invoices?</h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/invoices/new"
              className="rounded-xl bg-ica-accent px-6 py-3 text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90"
            >
              Set up my first follow-up
            </Link>
            <Link
              to="/sign-in"
              className="rounded-xl border border-ica-border px-6 py-3 text-sm font-medium text-ica-text-secondary transition-colors hover:text-ica-text"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-ica-border py-8">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-xs text-ica-text-muted">Invoice Chase Assistant</p>
          <p className="text-xs text-ica-text-muted">Built for freelancers.</p>
        </div>
      </footer>
    </div>
  )
}
